import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCreateProject } from "@/hooks/use-projects";

interface ProjectCreateDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ProjectCreateDialog({ open, onClose }: ProjectCreateDialogProps) {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!nombre.trim() || createProject.isPending) return;
    createProject.mutate(
      { nombre: nombre.trim(), descripcion: descripcion.trim() || undefined },
      {
        onSuccess: (project) => {
          setNombre("");
          setDescripcion("");
          onClose();
          navigate(`/proyectos/${project.id}`);
        },
      }
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nuevo proyecto"
      description="Crea un proyecto para agrupar documentos y conversaciones."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label
            htmlFor="project-nombre"
            className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
          >
            Nombre
          </label>
          <input
            id="project-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            required
            maxLength={200}
            autoFocus
            className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
            placeholder="Ej. Expediente 2026-001"
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="project-descripcion"
            className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
          >
            Descripción (opcional)
          </label>
          <textarea
            id="project-descripcion"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded border border-line bg-surface text-sm resize-none focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
            placeholder="Breve descripción del proyecto…"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={!nombre.trim() || createProject.isPending}
          >
            {createProject.isPending && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            Crear proyecto
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
